/**
 * Marketing Automation Dashboard
 */

import { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from "@mui/material";
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PersonAdd as PersonAddIcon
} from "@mui/icons-material";

// Argon Dashboard components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

function MarketingAutomation() {
  const [tabValue, setTabValue] = useState(0);
  const [triggers, setTriggers] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [dripCampaigns, setDripCampaigns] = useState([]);
  
  // Dialog states
  const [createTriggerDialog, setCreateTriggerDialog] = useState(false);
  const [createWorkflowDialog, setCreateWorkflowDialog] = useState(false);
  
  // Form states
  const [triggerForm, setTriggerForm] = useState({
    name: "",
    description: "",
    trigger_type: "",
    conditions: {},
    actions: [],
    is_active: true
  });
  
  const [workflowForm, setWorkflowForm] = useState({
    name: "",
    description: "",
    trigger_event: "",
    conditions: {},
    actions: [],
    is_active: true
  });

  // Predefined trigger types and events
  const triggerTypes = [
    { value: "rent_payment_overdue", label: "Rent Payment Overdue" },
    { value: "lease_expiring", label: "Lease Expiring" },
    { value: "maintenance_request_submitted", label: "Maintenance Request Submitted" },
    { value: "tenant_move_in", label: "Tenant Move In" },
    { value: "tenant_move_out", label: "Tenant Move Out" },
    { value: "property_vacancy", label: "Property Vacancy" },
    { value: "inspection_due", label: "Inspection Due" }
  ];

  const actionTypes = [
    { value: "send_email", label: "Send Email", icon: <EmailIcon /> },
    { value: "send_sms", label: "Send SMS", icon: <SmsIcon /> },
    { value: "add_to_contact_list", label: "Add to Contact List", icon: <PersonAddIcon /> },
    { value: "create_task", label: "Create Task", icon: <SettingsIcon /> },
    { value: "schedule_follow_up", label: "Schedule Follow-up", icon: <ScheduleIcon /> }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load triggers
      const triggersResponse = await fetch('/api/v1/marketing/triggers/');
      const triggersData = await triggersResponse.json();
      setTriggers(triggersData);
      
      // Load workflows
      const workflowsResponse = await fetch('/api/v1/marketing/workflows/');
      const workflowsData = await workflowsResponse.json();
      setWorkflows(workflowsData);
      
    } catch (error) {
      console.error('Error loading automation data:', error);
    }
  };

  const handleCreateTrigger = async () => {
    try {
      const response = await fetch('/api/v1/marketing/triggers/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(triggerForm)
      });
      
      if (response.ok) {
        setCreateTriggerDialog(false);
        setTriggerForm({
          name: "",
          description: "",
          trigger_type: "",
          conditions: {},
          actions: [],
          is_active: true
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating trigger:', error);
    }
  };

  const handleCreateWorkflow = async () => {
    try {
      const response = await fetch('/api/v1/marketing/workflows/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowForm)
      });
      
      if (response.ok) {
        setCreateWorkflowDialog(false);
        setWorkflowForm({
          name: "",
          description: "",
          trigger_event: "",
          conditions: {},
          actions: [],
          is_active: true
        });
        loadData();
      }
    } catch (error) {
      console.error('Error creating workflow:', error);
    }
  };

  const handleToggleTrigger = async (triggerId) => {
    try {
      await fetch(`/api/v1/marketing/triggers/${triggerId}/toggle`, {
        method: 'PUT'
      });
      loadData();
    } catch (error) {
      console.error('Error toggling trigger:', error);
    }
  };

  const addActionToForm = (formType, actionType) => {
    const newAction = {
      type: actionType,
      // Default properties based on action type
      ...(actionType === 'send_email' && {
        subject: "",
        content: "",
        template_id: null
      }),
      ...(actionType === 'send_sms' && {
        message: "",
        template_id: null
      }),
      ...(actionType === 'add_to_contact_list' && {
        contact_list_id: null
      }),
      ...(actionType === 'create_task' && {
        title: "",
        description: "",
        assigned_to: null
      }),
      ...(actionType === 'schedule_follow_up' && {
        delay_days: 1,
        action_type: "email"
      })
    };

    if (formType === 'trigger') {
      setTriggerForm(prev => ({
        ...prev,
        actions: [...prev.actions, newAction]
      }));
    } else {
      setWorkflowForm(prev => ({
        ...prev,
        actions: [...prev.actions, newAction]
      }));
    }
  };

  const removeActionFromForm = (formType, index) => {
    if (formType === 'trigger') {
      setTriggerForm(prev => ({
        ...prev,
        actions: prev.actions.filter((_, i) => i !== index)
      }));
    } else {
      setWorkflowForm(prev => ({
        ...prev,
        actions: prev.actions.filter((_, i) => i !== index)
      }));
    }
  };

  const renderTriggers = () => (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonTypography variant="h5">Marketing Triggers</ArgonTypography>
        <ArgonButton
          variant="gradient"
          color="info"
          startIcon={<AddIcon />}
          onClick={() => setCreateTriggerDialog(true)}
        >
          Create Trigger
        </ArgonButton>
      </ArgonBox>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Trigger Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Triggered</TableCell>
              <TableCell>Controls</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {triggers.map((trigger) => (
              <TableRow key={trigger.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {trigger.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {trigger.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={trigger.trigger_type.replace('_', ' ')} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    {trigger.actions.slice(0, 3).map((action, index) => (
                      <Chip 
                        key={index}
                        label={action.type.replace('_', ' ')} 
                        size="small"
                        color="primary"
                      />
                    ))}
                    {trigger.actions.length > 3 && (
                      <Chip 
                        label={`+${trigger.actions.length - 3} more`} 
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={trigger.is_active ? "Active" : "Inactive"} 
                    color={trigger.is_active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {trigger.last_triggered 
                    ? new Date(trigger.last_triggered).toLocaleDateString()
                    : "Never"
                  }
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color={trigger.is_active ? "warning" : "success"}
                    onClick={() => handleToggleTrigger(trigger.id)}
                  >
                    {trigger.is_active ? <PauseIcon /> : <PlayIcon />}
                  </IconButton>
                  <IconButton size="small" color="secondary">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </ArgonBox>
  );

  const renderWorkflows = () => (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ArgonTypography variant="h5">Automation Workflows</ArgonTypography>
        <ArgonButton
          variant="gradient"
          color="success"
          startIcon={<AddIcon />}
          onClick={() => setCreateWorkflowDialog(true)}
        >
          Create Workflow
        </ArgonButton>
      </ArgonBox>
      
      <Grid container spacing={3}>
        {workflows.map((workflow) => (
          <Grid item xs={12} md={6} lg={4} key={workflow.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6">
                    {workflow.name}
                  </Typography>
                  <Chip 
                    label={workflow.is_active ? "Active" : "Inactive"} 
                    color={workflow.is_active ? "success" : "default"}
                    size="small"
                  />
                </Box>
                
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {workflow.description}
                </Typography>
                
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary">
                    Trigger Event:
                  </Typography>
                  <Typography variant="body2">
                    {workflow.trigger_event.replace('_', ' ')}
                  </Typography>
                </Box>
                
                <Box mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    Actions: {workflow.actions.length}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                    {workflow.actions.slice(0, 2).map((action, index) => (
                      <Chip 
                        key={index}
                        label={action.type.replace('_', ' ')} 
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box mt={2} display="flex" justifyContent="space-between">
                  <Typography variant="caption" color="textSecondary">
                    Last executed: {workflow.last_executed 
                      ? new Date(workflow.last_executed).toLocaleDateString()
                      : "Never"
                    }
                  </Typography>
                  <Box>
                    <IconButton size="small" color="secondary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="info">
                      <TrendingUpIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </ArgonBox>
  );

  const renderAnalytics = () => (
    <ArgonBox>
      <ArgonTypography variant="h5" mb={3}>Automation Analytics</ArgonTypography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main">
                {triggers.filter(t => t.is_active).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Triggers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main">
                {workflows.filter(w => w.is_active).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Workflows
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main">
                {triggers.filter(t => t.last_triggered).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Triggers Fired This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" color="error.main">
                {workflows.filter(w => w.last_executed).length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Workflows Executed This Month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </ArgonBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <ArgonBox p={3}>
                  <ArgonTypography variant="h4" fontWeight="medium">
                    Marketing Automation
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Set up automated marketing workflows and triggers
                  </ArgonTypography>
                </ArgonBox>
              </Card>
            </Grid>
          </Grid>
        </ArgonBox>

        <ArgonBox>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label={`Triggers (${triggers.length})`} icon={<AutoAwesomeIcon />} />
              <Tab label={`Workflows (${workflows.length})`} icon={<SettingsIcon />} />
              <Tab label="Analytics" icon={<TrendingUpIcon />} />
            </Tabs>
          </Box>
          
          <ArgonBox mt={3}>
            {tabValue === 0 && renderTriggers()}
            {tabValue === 1 && renderWorkflows()}
            {tabValue === 2 && renderAnalytics()}
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      {/* Create Trigger Dialog */}
      <Dialog open={createTriggerDialog} onClose={() => setCreateTriggerDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Marketing Trigger</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trigger Name"
                value={triggerForm.name}
                onChange={(e) => setTriggerForm({...triggerForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={triggerForm.description}
                onChange={(e) => setTriggerForm({...triggerForm, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  value={triggerForm.trigger_type}
                  onChange={(e) => setTriggerForm({...triggerForm, trigger_type: e.target.value})}
                >
                  {triggerTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={triggerForm.is_active}
                    onChange={(e) => setTriggerForm({...triggerForm, is_active: e.target.checked})}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Actions</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {actionTypes.map((actionType) => (
                  <Button
                    key={actionType.value}
                    size="small"
                    variant="outlined"
                    startIcon={actionType.icon}
                    onClick={() => addActionToForm('trigger', actionType.value)}
                  >
                    {actionType.label}
                  </Button>
                ))}
              </Box>
              <List>
                {triggerForm.actions.map((action, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {actionTypes.find(at => at.value === action.type)?.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={action.type.replace('_', ' ')}
                      secondary={`Action ${index + 1}`}
                    />
                    <IconButton 
                      color="error" 
                      onClick={() => removeActionFromForm('trigger', index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTriggerDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateTrigger} variant="contained">
            Create Trigger
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Workflow Dialog */}
      <Dialog open={createWorkflowDialog} onClose={() => setCreateWorkflowDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Automation Workflow</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Workflow Name"
                value={workflowForm.name}
                onChange={(e) => setWorkflowForm({...workflowForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={workflowForm.description}
                onChange={(e) => setWorkflowForm({...workflowForm, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Trigger Event</InputLabel>
                <Select
                  value={workflowForm.trigger_event}
                  onChange={(e) => setWorkflowForm({...workflowForm, trigger_event: e.target.value})}
                >
                  {triggerTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={workflowForm.is_active}
                    onChange={(e) => setWorkflowForm({...workflowForm, is_active: e.target.checked})}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Actions</Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {actionTypes.map((actionType) => (
                  <Button
                    key={actionType.value}
                    size="small"
                    variant="outlined"
                    startIcon={actionType.icon}
                    onClick={() => addActionToForm('workflow', actionType.value)}
                  >
                    {actionType.label}
                  </Button>
                ))}
              </Box>
              <List>
                {workflowForm.actions.map((action, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      {actionTypes.find(at => at.value === action.type)?.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={action.type.replace('_', ' ')}
                      secondary={`Action ${index + 1}`}
                    />
                    <IconButton 
                      color="error" 
                      onClick={() => removeActionFromForm('workflow', index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateWorkflowDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateWorkflow} variant="contained">
            Create Workflow
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default MarketingAutomation;